import clsx from 'clsx';
import React from 'react';
import toast from 'react-hot-toast';

import IconCheckMarkRounded from '@components/Images/Rebranding/IconCheckMarkRounded';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/constants';
import getSubscriptionPriceBreakdown from '@utils/getSubscriptionPriceBreakdown';
import { renewCancelText } from '@utils/offerHelper';

import Tag from './Tag';

interface OfferProps {
  isSingleOffer?: boolean;
  offer: OfferOption;
  selectedOfferCode: string;
  setSelectedOffer?: (plan: SelectedOffer) => void;
}

const Offer = ({
  isSingleOffer,
  offer: {
    billingFrequency,
    billingFrequencyOverride,
    code,
    currentPrice,
    currentPriceDisplayOverride,
    description,
    isPro,
    isBestValue,
    originalPrice,
    originalPriceDisplayOverride,
    title,
  },
  selectedOfferCode,
  setSelectedOffer,
}: OfferProps) => {
  const { trackEvent } = useEventTracking();
  const isSelected = selectedOfferCode === code;
  const { currentPriceToDisplay, hasDiscount, originalPriceToDisplay } =
    getSubscriptionPriceBreakdown({
      currentPrice,
      currentPriceDisplayOverride,
      originalPrice,
      originalPriceDisplayOverride,
    });

  const {
    user: { dobVerified, isYPEligible },
  } = useSubscribeForm();

  const handleOfferSelection = () => {
    setSelectedOffer({
      description,
      billingFrequency,
      code,
      currentPrice,
      isPro,
      originalPrice,
      title,
    });
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: code }));

    const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
    if (isYPPlan && !isYPEligible && dobVerified) {
      toast.success(
        'You are not eligible for the Young Professionals plan. Please select another plan.'
      );
    }
  };

  return (
    <div
      id={code}
      onClick={isSingleOffer ? undefined : handleOfferSelection}
      className={clsx('relative block border-2 border-solid border-grayscale-beige p-24', {
        'selected border-primary-magenta': isSelected && !isPro,
        'selected border-pro-gold': isSelected && isPro,
        'cursor-pointer': !isSingleOffer,
      })}
    >
      {isSelected && (
        <IconCheckMarkRounded
          className={clsx('absolute -left-[17px] -top-[17px] w-[34px]', {
            'text-pro-gold': isPro,
            'text-primary-magenta': !isPro,
          })}
        />
      )}
      <div className="flex flex-col-reverse items-start sm:flex-row sm:items-center">
        <h2
          className={clsx(
            'm-0 font-suisse-works text-headline-xxs text-black sm:text-headline-xs',
            {
              'text-primary-magenta': isSelected,
              'text-pro-gold': isPro,
            }
          )}
        >
          {title}
        </h2>
        {(isBestValue || isSingleOffer) && (
          <Tag name="Best Value" className="best-value mb-16 sm:mb-0 sm:ml-24" />
        )}
      </div>
      <div className="mt-16 flex flex-wrap items-center">
        <span className="font-suisse-screen text-headline-alt-sm font-medium text-black">
          ${currentPriceToDisplay.toFixed(2)}
        </span>{' '}
        {hasDiscount && (
          <span className="ml-8 font-suisse-screen text-disclosure-lg text-grayscale-gray line-through sm:ml-16">
            ${originalPriceToDisplay.toFixed(2)}
          </span>
        )}
        {billingFrequencyOverride && (
          <span className="ml-8 font-suisse-screen text-disclosure-lg text-grayscale-charcoal sm:ml-16">
            {billingFrequencyOverride}
          </span>
        )}
      </div>
      <div className="mt-24 font-suisse-neue text-body-md text-grayscale-gray sm:text-body-lg">
        {renewCancelText(description)}
      </div>
    </div>
  );
};

export default Offer;
